import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [error, setError] = useState('');
  
  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setError('無効な確認リンクです。');
        return;
      }
      
      try {
        // バックエンドでメール確認とログインを実行
        const response = await loginWithToken(token);
        
        if (response && response.user) {
          setStatus('success');
          
          // 3秒後にダッシュボードにリダイレクト
          setTimeout(() => {
            const dashboardUrl = response.user.user_type === 'admin' 
              ? '/admin/dashboard' 
              : response.user.user_type === 'employer'
                ? '/employer/dashboard'
                : '/seeker/dashboard';
            navigate(dashboardUrl);
          }, 3000);
        } else {
          throw new Error('認証に失敗しました');
        }
      } catch (err) {
        console.error('Email confirmation error:', err);
        setStatus('error');
        setError('メールアドレスの確認中にエラーが発生しました。リンクの有効期限が切れているか、無効なリクエストです。');
      }
    };
    
    verifyEmail();
  }, [searchParams, navigate, loginWithToken]);
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  const handleResendEmail = () => {
    // メール再送信ロジックを実装
    // 例: navigate('/resend-verification');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">メールアドレスを確認中です</h3>
              <p className="mt-2 text-sm text-gray-600">少々お待ちください...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircleIcon className="h-10 w-10 text-green-600" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">メールアドレスの確認が完了しました</h3>
              <p className="mt-2 text-sm text-gray-600">
                ログインに成功しました。自動的にダッシュボードに移動します...
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  ホームに戻る
                </button>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationCircleIcon className="h-10 w-10 text-red-600" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">確認に失敗しました</h3>
              <p className="mt-2 text-sm text-gray-600">
                {error}
              </p>
              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={handleBackToHome}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  ホームに戻る
                </button>
                <button
                  type="button"
                  onClick={handleResendEmail}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  確認メールを再送する
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
